import { useState, forwardRef } from "react";
import JobIdIcon from "../../ui/icons/JobIdIcon";
import CandidateNameIcon from "../../ui/icons/CandidateNameIcon";
import EmailIcon from "../../ui/icons/EmailIcon";
import GenerateLinkIcon from "../../ui/icons/GenerateLinkIcon";
import StandardButton from "../../ui/Button/StandardButton";
import Modal from "../../ui/Modal";
import Toast from "../../ui/Toast";
import LinkGeneratedDialog from "../LinkGeneratedDialog";
import { interviewService } from "../../../services";
import { ValidationError } from "../../../types/interview";
import { debugError } from "../../../utils/debug";

interface CreatedSession {
  id: string;
  jobId: string;
  candidateName: string;
  email: string;
  interviewUrl: string; // Now from API response
}

interface TouchedFields {
  jobId: boolean;
  firstName: boolean;
  lastName: boolean;
  email: boolean;
}

interface ValidationErrors {
  jobId: string;
  firstName: string;
  lastName: string;
  email: string;
}

const errorMessageStyle: React.CSSProperties = {
  color: "#E11313",
  fontFamily: "Interstate",
  fontSize: "9px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "normal",
};

const errorContainerStyle: React.CSSProperties = {
  minHeight: "40px",
  marginTop: "0px",
};

const capitalizeFirstLetter = (value: string): string => {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
};

interface ToastState {
  show: boolean;
  type: "success" | "failed";
  message: string;
}

interface NewInterviewSessionProps {
  onSuccess?: () => void;
}

const NewInterviewSession = forwardRef<HTMLInputElement, NewInterviewSessionProps>(({ onSuccess }, ref) => {
  const [jobId, setJobId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [toast, setToast] = useState<ToastState>({ show: false, type: "success", message: "" });
  const [createdSession, setCreatedSession] = useState<CreatedSession | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [touched, setTouched] = useState<TouchedFields>({
    jobId: false,
    firstName: false,
    lastName: false,
    email: false,
  });

  const validateJobId = (value: string): string => {
    if (!value.trim()) return "Please enter a Job ID.";
    return "";
  };

  const namePattern = /^[a-zA-ZàáâãäåæçèéêëìíîïðñòóôõöøùúûüýÿßąęłńśźżÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖØÙÚÛÜÝŸĄĘŁŃŚŹŻ\-'. ]+$/;

  const validateFirstName = (value: string): string => {
    if (!value.trim()) return "Please enter candidate's first name.";
    if (!namePattern.test(value)) return "Please enter candidate's first name.";
    return "";
  };

  const validateLastName = (value: string): string => {
    if (!value.trim()) return "Please enter candidate's last name.";
    if (!namePattern.test(value)) return "Please enter candidate's last name.";
    return "";
  };

  const validateEmail = (value: string): string => {
    if (!value.trim()) return "Please enter candidate's email address.";
    // Safe regex pattern - uses specific character classes to prevent ReDoS
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(value)) return "Please enter a valid email address.";
    return "";
  };

  const getErrors = (): ValidationErrors => ({
    jobId: validateJobId(jobId),
    firstName: validateFirstName(firstName),
    lastName: validateLastName(lastName),
    email: validateEmail(email),
  });

  const handleBlur = (field: keyof TouchedFields) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const formatValidationErrors = (errors: Record<string, string[]>): string => {
    const messages: string[] = [];
    Object.entries(errors).forEach(([, fieldErrors]) => {
      messages.push(...fieldErrors);
    });
    return messages.join(". ");
  };

  const handleGenerateLink = async () => {
    // Mark all fields as touched
    setTouched({
      jobId: true,
      firstName: true,
      lastName: true,
      email: true,
    });

    const errors = getErrors();
    const hasErrors = Object.values(errors).some((error) => error !== "");

    if (hasErrors) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await interviewService.create({
        jobId,
        firstName,
        lastName,
        email,
      });

      const candidateName = `${firstName} ${lastName}`;

      setCreatedSession({
        id: response.id,
        jobId,
        candidateName,
        email,
        interviewUrl: response.interviewUrl, // Use URL from backend
      });
      setShowDialog(true);

      // Reset form
      setJobId("");
      setFirstName("");
      setLastName("");
      setEmail("");
      setTouched({
        jobId: false,
        firstName: false,
        lastName: false,
        email: false,
      });

      // Notify parent of successful creation
      onSuccess?.();
    } catch (error) {
      debugError("Failed to generate interview link:", error);

      if (error instanceof ValidationError) {
        const errorMessage = formatValidationErrors(error.errors);
        setToast({
          show: true,
          type: "failed",
          message: errorMessage || "Validation failed. Please check your input.",
        });
      } else {
        setToast({
          show: true,
          type: "failed",
          message: "Failed to create interview. Please try again.",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
  };

  const handleCopyLink = () => {
    setToast({
      show: true,
      type: "success",
      message: "Interview link copied successfully!",
    });
  };

  const handleToastClose = () => {
    setToast((prev) => ({ ...prev, show: false }));
  };

  const errors = getErrors();

  return (
    <div
      style={{
        borderRadius: "8px",
        border: "1px solid #E5E7EB",
        background: "#FFF",
        padding: "24px 24px 16px 24px",
      }}
    >
      {/* Header */}
      <div
        style={{
          color: "var(--color-charcoal, #63615C)",
          fontFamily: "Interstate",
          fontSize: "12px",
          fontStyle: "normal",
          fontWeight: 500,
          lineHeight: "16px",
          letterSpacing: "0.6px",
          textTransform: "uppercase",
          borderBottom: "1px solid #F3F4F6",
          background: "rgba(0, 0, 0, 0.00)",
          paddingBottom: "16px",
          marginLeft: "-24px",
          marginRight: "-24px",
          paddingLeft: "24px",
          paddingRight: "24px",
        }}
      >
        New Interview Session
      </div>

      {/* Form Row */}
      <div
        style={{
          padding: "24px 0 0 0",
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          alignItems: "flex-end",
        }}
      >
        {/* Column 1: Job ID - 22% (266px/1200px) */}
        <div style={{ width: "22%", minWidth: "150px", flexGrow: 1 }}>
          <div className="flex flex-col gap-2">
            <label htmlFor="jobId" className="input-label">
              <JobIdIcon />
              Job ID
            </label>
            <input
              id="jobId"
              ref={ref}
              type="text"
              placeholder="ABC005511"
              value={jobId}
              onChange={(e) => {
                const value = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
                setJobId(value);
              }}
              onBlur={() => handleBlur("jobId")}
              maxLength={50}
              className="standard-input"
            />
          </div>
          <div style={errorContainerStyle}>
            {touched.jobId && errors.jobId && (
              <span style={errorMessageStyle}>{errors.jobId}</span>
            )}
          </div>
        </div>

        {/* Columns 2 & 3: First Name + Last Name - grouped together (26% combined) */}
        <div
          style={{
            width: "26%",
            minWidth: "250px",
            flexGrow: 1,
            display: "flex",
            gap: "0px",
            alignItems: "end"
          }}
        >
          {/* First Name */}
          <div style={{ flex: 1 }}>
            <div className="flex flex-col gap-2">
              <label htmlFor="firstName" className="input-label">
                <CandidateNameIcon />
                Candidate Name
              </label>
              <input
                id="firstName"
                type="text"
                placeholder="First Name"
                value={firstName}
                aria-label="First Name"
                onChange={(e) => {
                  const value = e.target.value.replace(/[^a-zA-ZàáâãäåæçèéêëìíîïðñòóôõöøùúûüýÿßąęłńśźżÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖØÙÚÛÜÝŸĄĘŁŃŚŹŻ\-'. ]/g, "");
                  setFirstName(capitalizeFirstLetter(value));
                }}
                onBlur={() => handleBlur("firstName")}
                className="standard-input standard-input-left"
              />
            </div>
            <div style={errorContainerStyle}>
              {touched.firstName && errors.firstName && (
                <span style={errorMessageStyle}>{errors.firstName}</span>
              )}
            </div>
          </div>

          {/* Last Name */}
          <div style={{ flex: 1 }}>
            <div className="flex flex-col gap-2">
              <span className="input-label" style={{ visibility: "hidden" }} aria-hidden="true">
                &nbsp;
              </span>
              <input
                id="lastName"
                type="text"
                placeholder="Last Name"
                value={lastName}
                aria-label="Last Name"
                onChange={(e) => {
                  const value = e.target.value.replace(/[^a-zA-ZàáâãäåæçèéêëìíîïðñòóôõöøùúûüýÿßąęłńśźżÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖØÙÚÛÜÝŸĄĘŁŃŚŹŻ\-'. ]/g, "");
                  setLastName(capitalizeFirstLetter(value));
                }}
                onBlur={() => handleBlur("lastName")}
                className="standard-input standard-input-right"
              />
            </div>
            <div style={errorContainerStyle}>
              {touched.lastName && errors.lastName && (
                <span style={errorMessageStyle}>{errors.lastName}</span>
              )}
            </div>
          </div>
        </div>

        {/* Column 4: Email - 22% (266px/1200px) */}
        <div style={{ width: "22%", minWidth: "180px", flexGrow: 1 }}>
          <div className="flex flex-col gap-2">
            <label htmlFor="email" className="input-label">
              <EmailIcon />
              Email Address
            </label>
            <input
              id="email"
              type="text"
              placeholder="candidate@mail.com"
              value={email}
              onChange={(e) => {
                // Only allow valid email characters: alphanumeric, @, ., _, -, +
                const value = e.target.value.replace(/[^a-zA-Z0-9@.+_-]/g, "");
                setEmail(value);
              }}
              onBlur={() => handleBlur("email")}
              className="standard-input"
            />
          </div>
          <div style={errorContainerStyle}>
            {touched.email && errors.email && (
              <span style={errorMessageStyle}>{errors.email}</span>
            )}
          </div>
        </div>

        {/* Column 5: Button */}
        <div style={{ flexShrink: 0, minWidth: "fit-content" }}>
          <StandardButton
            text={isSubmitting ? "Generating..." : "Generate Interview Link"}
            startIcon={<GenerateLinkIcon />}
            variant="fill"
            color="primary"
            onClick={handleGenerateLink}
            disabled={isSubmitting}
          />
          <div style={errorContainerStyle} />
        </div>
      </div>

      {/* Link Generated Dialog */}
      {showDialog && createdSession && (
        <Modal
          width={800}
          showClose={true}
          onClose={handleCloseDialog}
          closeOnBackdropClick={false}
          closeOnEscape={false}
          body={
            <LinkGeneratedDialog
              jobId={createdSession.jobId}
              candidateName={createdSession.candidateName}
              email={createdSession.email}
              id={createdSession.id}
              interviewUrl={createdSession.interviewUrl}
              onClose={handleCloseDialog}
              onCopyLink={handleCopyLink}
            />
          }
        />
      )}

      {/* Toast Notification */}
      {toast.show && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={handleToastClose}
        />
      )}
    </div>
  );
});

NewInterviewSession.displayName = "NewInterviewSession";

export default NewInterviewSession;
